<template>
	<div class="cards_container is-flex">
		<CardVentas v-for="(card, index) in displayCards" :key="index" v-bind="card" class="mr-4">
			<template #extra>
				<div class="efficiency_badge">
					12%
				</div>
			</template>
		</CardVentas>
	</div>
</template>

<script>
import { mapState } from "vuex";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import { EXPENSE_CARD_PROGRESS_COLORS } from "@/views/financialReports/config/reportStructure";
import {
	formatPercent,
	formatRangeLabel,
	indicesForRange,
	monthsBetween,
} from "@/views/financialReports/utils/periodFormatting";

const EMPTY_CARD = () => ({
	period: "-",
	amount: 0,
	mom: "",
	yoy: "",
	progressItems: [],
});

export default {
	name: "ExpensesSummaryCards",

	components: { CardVentas },

	props: {
		monthHeaders: {
			type: Array,
			required: true,
		},
		tableRows: {
			type: Array,
			required: true,
		},
		totalRow: {
			type: Object,
			required: true,
		},
		isCuentasMode: {
			type: Boolean,
			required: true,
		},
	},

	computed: {
		...mapState("expensesReport", ["summaryCards"]),

		displayCards() {
			if (!this.summaryCards) {
				return [EMPTY_CARD(), EMPTY_CARD(), EMPTY_CARD()];
			}
			const { current_month, ytd, ltm } = this.summaryCards;
			return [
				this.buildCard(current_month, { showMom: true }),
				this.buildCard(ytd),
				this.buildCard(ltm),
			];
		},
	},

	methods: {
		buildCard(card, { showMom = false } = {}) {
			const indices = indicesForRange(this.monthHeaders, card.period_start, card.period_end);
			// Si el filtro de la tabla no cubre todo el período de la card,
			// el breakdown desde tableRows quedaría incompleto: lo omitimos.
			const fullyInRange = indices.length === monthsBetween(card.period_start, card.period_end);
			return {
				period: formatRangeLabel(card.period_start, card.period_end),
				amount: card.amount,
				mom: showMom ? formatPercent(card.mom) : "",
				yoy: formatPercent(card.yoy),
				progressItems: fullyInRange
					? this.topItemsForRange(indices, card.amount)
					: [],
			};
		},

		topItemsForRange(indices, totalAmount) {
			if (indices.length === 0 || totalAmount === 0) return [];

			const baseLevel = this.isCuentasMode ? 2 : 3;
			const baseRows = this.tableRows.filter((row) => row.level === baseLevel);

			// Agrupar por etiqueta — una contraparte puede repetirse en varias categorías
			const aggregated = {};
			baseRows.forEach((row) => {
				if (!aggregated[row.label]) {
					aggregated[row.label] = new Array(row.values.length).fill(0);
				}
				row.values.forEach((val, i) => {
					aggregated[row.label][i] += (val || 0);
				});
			});

			let result = Object.keys(aggregated).map((label, i) => {
				const values = aggregated[label];
				const rangeAmount = indices.reduce((sum, idx) => sum + (values[idx] || 0), 0);
				return {
					label,
					amount: rangeAmount,
					percent: Math.round((rangeAmount / totalAmount) * 100),
					color: EXPENSE_CARD_PROGRESS_COLORS[i % EXPENSE_CARD_PROGRESS_COLORS.length],
				};
			});

			if (!this.isCuentasMode) {
				result.sort((a, b) => b.amount - a.amount);
			}

			return result.slice(0, 3);
		},
	},
};
</script>

<style scoped lang="scss">
.cards_container {
	width: 100%;
}

.efficiency_badge {
	display: flex;
	align-items: center;
	background: #fef3c7;
	color: #b45309;
	padding: 4px 10px;
	border-radius: 20px;
	font-size: 12px;
	font-weight: 700;
}
</style>
